import { createAsyncThunk } from '@reduxjs/toolkit'

import { selectGenerateContentLimits } from '~publish/legacy/ai/state/aiLimits/slice'
import { client } from '~publish/legacy/apollo-client'
import { graphql } from '~publish/graphql'
import { selectCurrentOrganizationId } from '~publish/legacy/organizations/selectors'
import type { AppDispatch, RootState } from '~publish/legacy/store'

type TGenerateIdeasProps = {
  prompt: string
  cta: string
}

type TGenerateIdeasVariables = {
  organizationId: string
  config: {
    prompt: string
    cta: string
  }
}

type TSuggestion = {
  content: string
}

type TResponse = {
  suggestions: TSuggestion[]
  code?: string
  success: boolean
  message: string
}

const GENERATE_IDEAS_MUTATION = graphql(/* GraphQL */ `
  mutation generateIdeas(
    $organizationId: String!
    $config: ContentGenerationInput!
  ) {
    generateIdeas(organizationId: $organizationId, config: $config) {
      ... on TextGenerated {
        suggestions {
          content
        }
      }
      ... on CommonError {
        code
        success
        message
        error
      }
    }
  }
`)

const generateIdeas = createAsyncThunk<
  TResponse | undefined,
  TGenerateIdeasProps,
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>(
  'ai/generateIdeas',
  async ({ prompt, cta }, { rejectWithValue, getState }) => {
    const state = getState()

    const { canGenerateContent } = selectGenerateContentLimits(state)
    if (!canGenerateContent) return

    try {
      const response = await client.mutate<
        {
          generateIdeas: TResponse
        },
        TGenerateIdeasVariables
      >({
        mutation: GENERATE_IDEAS_MUTATION,
        variables: {
          organizationId: selectCurrentOrganizationId(state),
          config: {
            prompt,
            cta,
          },
        },
      })

      return response.data?.generateIdeas
    } catch (error) {
      throw rejectWithValue(
        error instanceof Error ? error.message : String(error),
      )
    }
  },
)

export { GENERATE_IDEAS_MUTATION, generateIdeas, TSuggestion, TResponse }
